// WordPress REST API Service
// This service handles communication with WordPress backend

export interface WordPressPost {
  id: number;
  date: string;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  excerpt: {
    rendered: string;
  };
  featured_media: number;
  categories: number[];
  slug: string;
  link: string;
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url: string;
      alt_text: string;
    }>;
    'wp:term'?: Array<Array<{
      id: number;
      name: string;
      slug: string;
      taxonomy?: string;
    }>>;
  };
}

export interface WordPressCategory {
  id: number;
  name: string;
  slug: string;
  count: number;
}

export interface WordPressMedia {
  id: number;
  source_url: string;
  alt_text: string;
  media_details: {
    sizes: {
      thumbnail?: { source_url: string };
      medium?: { source_url: string };
      large?: { source_url: string };
      full?: { source_url: string };
    };
  };
}

class WordPressService {
  private baseUrl: string;
  private apiUrl: string;
  private username: string;
  private password: string;

  constructor() {
    // Configure your WordPress site URL here
    this.baseUrl = process.env.REACT_APP_WORDPRESS_URL || 'https://your-wordpress-site.com';
    
    // Use CORS proxy for development if needed
    const isDevelopment = process.env.NODE_ENV === 'development';
    const corsProxy = process.env.REACT_APP_CORS_PROXY || 'https://cors-anywhere.herokuapp.com/';
    
    if (isDevelopment && process.env.REACT_APP_USE_CORS_PROXY === 'true') {
      this.apiUrl = `${corsProxy}${this.baseUrl}/wp-json/wp/v2`;
    } else {
      this.apiUrl = `${this.baseUrl}/wp-json/wp/v2`;
    }

    // WordPress credentials
    this.username = process.env.REACT_APP_WORDPRESS_USERNAME || '';
    this.password = process.env.REACT_APP_WORDPRESS_PASSWORD || '';
  }

  /**
   * Create authenticated headers for WordPress API requests
   */
  private getAuthHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add Basic Authentication if credentials are provided
    if (this.username && this.password) {
      const credentials = btoa(`${this.username}:${this.password}`);
      headers['Authorization'] = `Basic ${credentials}`;
      console.log('Using WordPress authentication with username:', this.username);
    } else {
      console.log('No WordPress credentials provided - using public access');
    }

    return headers;
  }

  /**
   * Test WordPress API connection and authentication
   */
  async testConnection(): Promise<{ success: boolean; message: string; status?: number }> {
    try {
      const url = `${this.apiUrl}/posts?per_page=1`;
      console.log('Testing WordPress API connection:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      if (response.ok) {
        return { 
          success: true, 
          message: 'WordPress API connection successful!',
          status: response.status
        };
      } else {
        return { 
          success: false, 
          message: `WordPress API error: ${response.status} ${response.statusText}`,
          status: response.status
        };
      }
    } catch (error) {
      console.error('WordPress API connection test failed:', error);
      return { 
        success: false, 
        message: `Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Fetch posts from WordPress
   */
  async getPosts(params: {
    page?: number;
    per_page?: number;
    categories?: number[];
    search?: string;
    orderby?: string;
    order?: 'asc' | 'desc';
  } = {}): Promise<WordPressPost[]> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.per_page) queryParams.append('per_page', params.per_page.toString());
      if (params.categories && params.categories.length > 0) {
        queryParams.append('categories', params.categories.join(','));
      }
      if (params.search) queryParams.append('search', params.search);
      if (params.orderby) queryParams.append('orderby', params.orderby);
      if (params.order) queryParams.append('order', params.order);

      // Include featured media and categories in response
      queryParams.append('_embed', '1');
      
      const url = `${this.apiUrl}/posts?${queryParams.toString()}`;
      console.log('Fetching WordPress posts from:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('WordPress API Error:', {
          status: response.status,
          statusText: response.statusText,
          url: url,
          response: errorText
        });
        
        if (response.status === 401) {
          throw new Error(`WordPress REST API access denied (401). Please check:
1. Permalink settings (not set to "Plain")
2. Security plugins (Wordfence, iThemes, etc.)
3. .htaccess file restrictions
4. WordPress REST API permissions`);
        } else if (response.status === 403) {
          throw new Error(`WordPress REST API forbidden (403). Please check:
1. Security plugin settings
2. REST API restrictions
3. Server firewall rules`);
        } else {
          throw new Error(`WordPress API error: ${response.status} ${response.statusText}`);
        }
      }
      
      const posts: WordPressPost[] = await response.json();
      console.log(`Successfully fetched ${posts.length} posts from WordPress`);
      return posts;
    } catch (error) {
      console.error('Error fetching WordPress posts:', error);
      throw error;
    }
  }

  /**
   * Fetch categories from WordPress
   */
  async getCategories(): Promise<WordPressCategory[]> {
    try {
      const url = `${this.apiUrl}/categories?per_page=100`;
      console.log('Fetching WordPress categories from:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('WordPress Categories API Error:', {
          status: response.status,
          statusText: response.statusText,
          url: url,
          response: errorText
        });
        
        if (response.status === 401) {
          throw new Error(`WordPress categories API access denied (401). Please check REST API permissions.`);
        } else {
          throw new Error(`WordPress categories API error: ${response.status} ${response.statusText}`);
        }
      }
      
      const categories: WordPressCategory[] = await response.json();
      console.log(`Successfully fetched ${categories.length} categories from WordPress`);
      return categories;
    } catch (error) {
      console.error('Error fetching WordPress categories:', error);
      throw error;
    }
  }

  /**
   * Fetch a single post by ID
   */
  async getPost(id: number): Promise<WordPressPost> {
    try {
      const response = await fetch(`${this.apiUrl}/posts/${id}?_embed=1`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const post: WordPressPost = await response.json();
      return post;
    } catch (error) {
      console.error('Error fetching WordPress post:', error);
      throw error;
    }
  }

  /**
   * Search posts
   */
  async searchPosts(searchTerm: string, page: number = 1): Promise<WordPressPost[]> {
    return this.getPosts({
      search: searchTerm,
      page,
      per_page: 10,
      orderby: 'relevance'
    });
  }

  /**
   * Get posts by category slug
   */
  async getPostsByCategory(categorySlug: string, page: number = 1): Promise<WordPressPost[]> {
    try {
      // First get the category ID by slug
      const categoriesResponse = await fetch(`${this.apiUrl}/categories?slug=${categorySlug}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      const categories: WordPressCategory[] = await categoriesResponse.json();
      
      if (categories.length === 0) {
        return [];
      }
      
      const categoryId = categories[0].id;
      
      return this.getPosts({
        categories: [categoryId],
        page,
        per_page: 10,
        orderby: 'date',
        order: 'desc'
      });
    } catch (error) {
      console.error('Error fetching posts by category:', error);
      throw error;
    }
  }

  /**
   * Get featured image URL for a post
   */
  getFeaturedImageUrl(post: WordPressPost, size: 'thumbnail' | 'medium' | 'large' | 'full' = 'medium'): string | null {
    if (!post._embedded || !post._embedded['wp:featuredmedia']) {
      return null;
    }
    
    const media = post._embedded['wp:featuredmedia'][0];
    return media.source_url || null;
  }

  /**
   * Get authenticated image URL for a post
   * This method returns the same URL but indicates it needs authentication
   */
  getAuthenticatedImageUrl(post: WordPressPost, size: 'thumbnail' | 'medium' | 'large' | 'full' = 'medium'): string | null {
    if (!post._embedded || !post._embedded['wp:featuredmedia']) {
      return null;
    }
    
    const media = post._embedded['wp:featuredmedia'][0];
    return media.source_url || null;
  }

  /**
   * Get category names for a post
   */
  getPostCategories(post: WordPressPost): string[] {
    if (!post._embedded || !post._embedded['wp:term']) {
      return [];
    }
    
    // WordPress REST API returns terms in order, categories are typically first
    // We'll assume the first term array contains categories
    const categories = post._embedded['wp:term'][0];
    
    return categories ? categories.map(cat => cat.name) : [];
  }

  /**
   * Format WordPress date to readable format
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '.');
  }

  /**
   * Truncate text to specified length
   */
  truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }
}

// Create and export a singleton instance
export const wordpressService = new WordPressService();

export default wordpressService;
